/**
 * Household Service
 * Feature 008: Household Profile Management
 *
 * Provides CRUD operations for household-wide settings including:
 * - Budget configuration
 * - Family preferences
 * - Member profile management
 */

import type { HouseholdProfile, BudgetSettings, FamilyPreferences, UserProfile } from '../types';
import { logger } from '../utils/logger';

const STORAGE_KEY = 'householdProfile';

/**
 * Load household profile from localStorage
 * @returns HouseholdProfile or null if not found
 */
export function getHouseholdProfile(): HouseholdProfile | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as HouseholdProfile;
  } catch (error) {
    logger.error('Error loading household profile:', error);
    return null;
  }
}

/**
 * Save household profile to localStorage
 * @param profile - The household profile to save
 */
export function saveHouseholdProfile(profile: HouseholdProfile): void {
  try {
    profile.updatedAt = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch (error) {
    logger.error('Error saving household profile:', error);
    throw new Error('Failed to save household profile');
  }
}

/**
 * Initialize household with default values
 * @param memberIds - Array of UserProfile IDs to include in household
 * @returns Newly created HouseholdProfile
 */
export function initializeHousehold(memberIds: string[]): HouseholdProfile {
  const now = new Date().toISOString();
  const household: HouseholdProfile = {
    id: 'household-1',
    name: 'My Household',
    memberProfileIds: memberIds,
    preferences: {
      neverBuy: [],
      preferredMealTypes: [],
      dietaryFocus: undefined,
    },
    createdAt: now,
    updatedAt: now,
  };

  saveHouseholdProfile(household);
  return household;
}

/**
 * Update budget settings for household
 * @param budget - Budget settings to apply
 */
export function updateBudget(budget: BudgetSettings): void {
  const household = getHouseholdProfile();
  if (!household) {
    throw new Error('No household profile found. Initialize household first.');
  }

  household.budget = budget;
  saveHouseholdProfile(household);
}

/**
 * Update family preferences for household
 * @param preferences - Family preferences to apply
 */
export function updatePreferences(preferences: FamilyPreferences): void {
  const household = getHouseholdProfile();
  if (!household) {
    throw new Error('No household profile found. Initialize household first.');
  }

  household.preferences = preferences;
  saveHouseholdProfile(household);
}

/**
 * Get all member profiles for the household
 * @returns Array of UserProfile objects for household members
 */
export function getMemberProfiles(): UserProfile[] {
  const household = getHouseholdProfile();
  if (!household) return [];

  try {
    const profilesJson = localStorage.getItem('userProfiles');
    if (!profilesJson) return [];

    const allProfiles = JSON.parse(profilesJson) as UserProfile[];

    // Filter to only household members
    return allProfiles.filter(profile =>
      household.memberProfileIds.includes(profile.id)
    );
  } catch (error) {
    logger.error('Error loading member profiles:', error);
    return [];
  }
}

/**
 * Add a member to the household
 * @param profileId - ID of the UserProfile to add
 */
export function addMember(profileId: string): void {
  const household = getHouseholdProfile();
  if (!household) {
    throw new Error('No household profile found. Initialize household first.');
  }

  if (!household.memberProfileIds.includes(profileId)) {
    household.memberProfileIds.push(profileId);
    saveHouseholdProfile(household);
  }
}

/**
 * Remove a member from the household
 * @param profileId - ID of the UserProfile to remove
 */
export function removeMember(profileId: string): void {
  const household = getHouseholdProfile();
  if (!household) {
    throw new Error('No household profile found. Initialize household first.');
  }

  household.memberProfileIds = household.memberProfileIds.filter(
    id => id !== profileId
  );
  saveHouseholdProfile(household);
}

/**
 * Update household name
 * @param name - New name for the household
 */
export function updateHouseholdName(name: string): void {
  const household = getHouseholdProfile();
  if (!household) {
    throw new Error('No household profile found. Initialize household first.');
  }

  household.name = name;
  saveHouseholdProfile(household);
}
